// src/lib/transferStageActions.js
//
// #602 / gate 553 D28-D33, Session 79.21c. Which actions belong to which tab.
//
// ── THE PROBLEM, in plain terms ──────────────────────────────────────────────
//
// Every tab on the Transfers page opened the SAME sheet with EVERY stage's
// buttons on it. A fabricator on "To make" who came to say "we made sixty" was
// offered a truck; a driver on "To load" was offered a production sign-off.
// Nothing was written wrongly -- each action is still gated by who you are and
// by what is physically standing there -- but a tab is a promise about the job
// in front of you, and the sheet did not keep it.
//
// D19 said so on Sep 14 and was built in halves: the sheet is TOLD its stage and
// uses it for card sub-lines and for the sentence naming who does the job. The
// half that removes the parallel actions is this file.
//
// ── WHY ONE COMPONENT CONDITIONED ON STAGE, RATHER THAN SEVEN SCREENS ────────
//
// The research split cleanly and the split IS the finding. THREE enterprise WMS
// platforms separate the SCREENS: SAP EWM's floor worker meets an RF transaction
// selected by the job and never the document; Oracle WMS Cloud ships
// task-specific mobile pages; Dynamics 365 goes furthest, with mobile device
// menu items configured one per task and assigned per user and warehouse.
// ServiceNow instead keeps ONE form and conditions each action on the record's
// state, evaluated at form load.
//
// Stede is ServiceNow's shape, deliberately. Splitting our sheet into seven
// would duplicate the record, the journey and Withdraw seven times -- and those
// three belong to the REQUEST rather than to any stage (D29).
//
// ServiceNow's at-load evaluation is also why D32 exists: a sheet opened from a
// notification has no tab behind it, and showing everything there would quietly
// restore the defect on the path a manager uses most.

// D28. The mapping, exactly as approved. Read by the component AND by the test,
// so the test cannot drift into being a second implementation of it (#514).
export const STAGE_ACTIONS = {
  confirm: ['approve'],
  to_make: ['signoff'],
  to_load: ['load'],
  // D30 -- the one exception to "one job per tab", and it is forced by where
  // the rows actually sit. #531 put anything still owed to your branch on "On
  // the way", and its own blurb says so. A balance with nothing rolling and
  // nothing at a yard is FINAL, and this is the tab holding it -- so putting
  // the write-off only on "To count in" would leave an owed transfer visible
  // with no way to end it, which is the dead end #559 exists to stop.
  moving: ['closeShort'],
  collect: ['collect'],
  count_in: ['receive', 'closeShort'],
  finished: [],
};

// D29. Not stage actions, and hiding them by stage would be the same mistake
// pointed the other way. The record and the journey are what make the sheet
// worth opening from anywhere; Withdraw is gated on WHO ASKED and whether
// anything has shipped -- gate 371 D31-D37 -- and taking a request back is
// exactly what somebody does after looking at it on whatever tab they found it
// on.
export const SHARED_ACTIONS = ['record', 'history', 'withdraw'];

// THE ONE LIST OF TAB NAMES -- #681, gate 665 D16, Session 79.22b.4.4. The
// Transfers page reads its tab labels from here, so a rename cannot land in one
// list and miss the other: there used to be two copies. (A page may import a
// lib; never the other way round.)
//
// "Out & owed" was "On the way", outcome A of D16, approved by Colby: the tab
// holds this branch's trucks still on the road, other branches' trucks, what is
// owed with nothing moving, and what this branch sent that came up short --
// and it never held the trucks actually arriving, which are on To count in
// (#681). My requests keeps "On the way" for the person who asked, which is
// the right words there; the clash goes because this tab stops using them.
export const TAB_LABELS = {
  confirm: 'Awaiting confirmation',
  to_make: 'To make',
  to_load: 'To load',
  moving: 'Out & owed',
  collect: 'To collect',
  count_in: 'To count in',
  finished: 'Finished',
};

// ── WHICH TAB TRANSFERS OPENS ON -- #689 first half, gate 665 D18 ───────────
//
// The first tab, in the order a transfer travels, where the person has
// something they can do. A manager with an ask to agree still opens on
// Awaiting confirmation, as before; Houston's fabricator with the sign-off
// switch opens on To make, its loader on To load, Dallas's driver on To
// collect. With nothing to do, the first tab holding anything; with nothing
// anywhere, Awaiting confirmation. Finished is never chosen -- it is history,
// never a job.
//
// `canAct` is built by the page from the SAME entitlements the sheet uses
// (canApproveTransfer, canSignOffProduction, canCloseTransferShort and the
// branch tests). It is advisory only: choosing a tab grants nothing. Until
// D28's driver switch exists (79.22b.5), anyone at a branch counts as able to
// collect or load there -- the gate says so, and no driver rule is invented.
export const OPENING_ORDER = ['confirm', 'to_make', 'to_load', 'moving', 'collect', 'count_in'];

export function openingTab({ canAct = {}, counts = {} } = {}) {
  const job = OPENING_ORDER.find(id => !!canAct[id]);
  if (job) return job;
  return OPENING_ORDER.find(id => Number(counts[id]) > 0) || 'confirm';
}

// The words the stage chip carries. Named in the gate's F2 copy table.
export const STAGE_CHIP = {
  confirm: 'Agreeing to this',
  to_make: 'Signing off production',
  to_load: 'Filling a truck',
  moving: 'On the road',
  collect: 'Fetching from a yard',
  count_in: 'Counting it in',
  finished: 'Finished',
};

// #726, Session 79.22b.6e -- THE COLLECT STAGE MET ON THE ROAD. Dallas's driver
// meeting Houston's truck at I-45 at Buffalo was told "Fetching from a yard"
// while every other line on the sheet named the road. The mirror of the handing
// driver's own sentence, "Handing over on the road at …" (#670); a pickup at a
// branch yard keeps the gate 553 F2 word. Approved as drawn (R2).
export const ROAD_COLLECT_CHIP = 'Taking over on the road';
export function stageChipFor(stage, { onRoad = false } = {}) {
  if (stage === 'collect' && onRoad) return ROAD_COLLECT_CHIP;
  return STAGE_CHIP[stage] || null;
}

// #725 with #730, Session 79.22b.6e -- THE LAST SECTION OF A SHEET SAYS WHAT
// IS TRUE. Approved as drawn (R2). The section heading named the stage the
// sheet was OPENED on, whether or not the transfer was still there: "Filling a
// truck" over nothing once Houston had sent everything, "Counting it in" with
// nothing left to count, "On the road" for a transfer nobody had loaded (the
// derivation's fall-through, read by a branch with no part in it), "Finished"
// over a refusal still waiting for its asker's decision.
//
// The heading is drawn when something is drawn under it, or when the transfer
// really is at that stage - `at` is a set of booleans about the transfer itself,
// computed by the sheet. When neither holds, the person whose job the stage was
// gets one true sentence (emptyStageSentence below); anybody else gets nothing,
// because the line above the history already tells them who acts.
export function stageIsTrue(stage, at = {}) {
  switch (stage) {
    case 'confirm':  return !!at.approvable;
    case 'to_make':  return !!at.unmade;
    case 'to_load':  return !!at.readyToLoad;
    case 'moving':   return !!at.moving;
    case 'collect':  return !!at.atPickup;
    case 'count_in': return !!at.arriving;
    case 'finished': return !!at.finished && !at.decisionPending;
    default:         return false;
  }
}

// The true sentence for the person whose job the stage was, when nothing is
// left for them on it. Gate 553 X2's own words at the loading end; the others
// are the drawing's. Null for everybody else, and for a receiving branch that
// has been sent nothing yet - the spectator line already says who sends it.
export function emptyStageSentence({
  stage, atSending = false, atReceiving = false, makeLeft = 0, madeAny = false,
  sent = 0, owed = 0, stillOnItsWay = null, senderName = null, receiverName = null,
} = {}) {
  const sender = senderName || 'the sending branch';
  const receiver = receiverName || 'the receiving branch';
  const tail = stillOnItsWay ? ` ${stillOnItsWay.charAt(0).toUpperCase()}${stillOnItsWay.slice(1)}.` : '';
  if (stage === 'to_load' && atSending) {
    if (makeLeft > 0) {
      return madeAny
        ? `Nothing is waiting to be loaded — everything made so far has gone. ${makeLeft} still to make.`
        : `Nothing has been made yet. ${makeLeft} still to make.`;
    }
    return owed > 0 ? 'Nothing is waiting to be loaded.' : 'Nothing is waiting to be loaded — everything has gone.';
  }
  if (stage === 'collect' && atReceiving) return `Nothing is left to collect.${tail}`;
  if ((stage === 'count_in' || stage === 'moving') && atReceiving && sent > 0) {
    if (stillOnItsWay) return `Nothing has reached ${receiver} to count in yet.${tail}`;
    if (makeLeft > 0) return `Everything sent so far is counted in. ${makeLeft} still to make at ${sender}.`;
    if (owed > 0) return `Everything sent so far is counted in. ${owed} still to send from ${sender}.`;
    return 'Everything sent so far is counted in.';
  }
  return null;
}

export function isKnownStage(stage) {
  return !!stage && Object.prototype.hasOwnProperty.call(STAGE_ACTIONS, stage);
}

// D32. A sheet opened WITHOUT a tab -- a notification, a deep link -- derives
// its stage from the transfer's own state: the first stage in tab order this
// transfer is actually at. An old link then lands somewhere honest rather than
// nowhere, and D31's signpost carries the reader on if the job they wanted is a
// stage further along.
//
// `at` is a set of booleans the caller has already computed, because every one
// of them is a question about the DATABASE ROW and this module stays pure.
//
// AN UNKNOWN STAGE IS TREATED AS NO STAGE, and that direction is deliberate:
// falling back to "show everything" would reopen the exact defect D28 closes.
export function deriveStage(at = {}) {
  if (at.isApprovable) return 'confirm';
  if (at.hasUnmadeLines) return 'to_make';
  if (at.isLoadable) return 'to_load';
  if (at.waitingAtHub) return 'collect';
  if (at.isReceivable) return 'count_in';
  if (at.isFinished) return 'finished';
  return 'moving';
}

// The question the component asks for each action.
export function allowsAction(stage, action) {
  if (SHARED_ACTIONS.includes(action)) return true;
  const effective = isKnownStage(stage) ? stage : null;
  // With no usable stage, nothing stage-specific is drawn. The caller is
  // expected to have derived one; this is the floor, not the normal path.
  if (!effective) return false;
  return STAGE_ACTIONS[effective].includes(action);
}

// D31. An action you are ENTITLED to, on a tab that is not its home, is not
// drawn -- and the sheet names the tab and offers one tap to it. An
// unexplained absence is the same puzzle as a dead press (#492), and this is
// also the half that keeps D28 inside the 2-taps-under-3-seconds floor: without
// it somebody on the wrong tab pays three, closing and re-tabbing and
// re-opening.
//
// Returns null when there is nothing to point at. Only ever called with actions
// the caller has already confirmed the person may perform.
export function signpostFor(stage, entitledActions = []) {
  if (!isKnownStage(stage)) return null;
  const elsewhere = entitledActions.filter(a => !allowsAction(stage, a));
  if (!elsewhere.length) return null;
  const action = elsewhere[0];
  const home = Object.keys(STAGE_ACTIONS).find(s => STAGE_ACTIONS[s].includes(action));
  if (!home) return null;
  return { action, stage: home, label: TAB_LABELS[home] };
}

// #655, Session 79.22b -- THE SIGNPOST'S WORDS, AS GATE 553 F2 APPROVED THEM:
// "{n} {are standing here ready to go | are still to make | are waiting at
// {yard} | are arriving here}. Open {tab}". The sheet drew fixed sentences with
// no count, and had NONE for To collect -- a collect signpost would have read
// "The balance on this can be written off." The count and the place are the
// point: they say what is waiting and where, before the tap. Approval and the
// write-off have no count to give and keep their plain sentences. Without a
// count the old sentence stands rather than a sentence with a hole in it.
// #673, Session 79.22b.4 -- RULED BY COLBY: "The system should say where a load
// is actually going and not use vague words like 'Here'." An admin at Dallas
// looking at Austin read "400 are arriving here" about a Houston -> Dallas
// transfer. `place` names the branch, taken from the TRANSFER by the caller,
// never from who is reading -- the way `collect` already took `yard`. With no
// place the sentence names none, rather than falling back to "here".
export function signpostSentence(signpost, { n = null, yard = null, place = null } = {}) {
  if (!signpost) return null;
  const count = Number(n);
  const has = Number.isFinite(count) && count > 0;
  const verb = count === 1 ? 'is' : 'are';
  switch (signpost.action) {
    case 'load':
      return has
        ? (place ? `${count} ${verb} standing at ${place} ready to go.` : `${count} ${verb} ready to go.`)
        : 'This is ready to load.';
    case 'receive':
      return has
        ? (place ? `${count} ${verb} arriving at ${place}.` : `${count} ${verb} arriving.`)
        : (place ? `This is arriving at ${place}.` : 'This is arriving.');
    case 'collect':
      return has && yard ? `${count} ${verb} waiting at ${yard}.` : 'This is waiting to be fetched.';
    case 'signoff':
      return has ? `${count} ${verb} still to make.` : 'This is still to make.';
    case 'approve':
      return 'This is waiting on your approval.';
    case 'closeShort':
      return 'The balance on this can be written off.';
    default:
      return null;
  }
}
